import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Calendar, DollarSign, FileText, Download, AlertCircle, CheckCircle, Clock, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const Billing = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    expiry: '',
    cvc: '',
    name: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (user?.id) {
      loadSubscriptionData();
      loadInvoices();
    }
  }, [user]);

  // Check for action parameter and auto-open modal
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'changePlan' && !isLoading) {
      setShowChangePlanModal(true);
      // Remove the parameter from URL
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, [searchParams, isLoading]);

  const loadSubscriptionData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/me`, {
        params: { user_id: user.id }
      });
      setSubscription(response.data);
      setSelectedPlan(response.data.tier);
    } catch (error) {
      console.error('Error loading subscription:', error);
      // Set default if error
      setSubscription({
        status: 'active',
        tier: 'pro',
        tier_display: 'Pro',
        price: 349,
        base_price: 349,
        discount_percent: 0,
        billing_cycle: 'monthly',
        user_limit: 5,
        next_billing_date: new Date(Date.now() + 30*24*60*60*1000).toISOString(),
        payment_method: {
          type: 'card',
          last4: '4242',
          brand: 'Visa'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/invoices`, {
        params: { user_id: user.id, limit: 6 }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      past_due: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: Clock }
    };
    
    const badge = badges[status] || badges.active;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (isLoading || !subscription) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing information</p>
      </div>

      {/* Current Subscription */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold">{subscription.tier_display} Plan</h2>
              {getStatusBadge(subscription.status)}
            </div>
            <p className="text-blue-100">Billed {subscription.billing_cycle}</p>
            {subscription.discount_percent > 0 && (
              <p className="text-yellow-200 text-sm font-semibold mt-1">
                🎉 Beta Discount: {subscription.discount_percent}% OFF
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{formatCurrency(subscription.price)}</div>
            <div className="text-blue-100">per month</div>
            {subscription.discount_percent > 0 && (
              <div className="text-blue-200 text-sm mt-1 line-through">
                {formatCurrency(subscription.base_price)}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-blue-400">
          <div>
            <div className="text-blue-100 text-sm mb-1">Next Billing Date</div>
            <div className="font-semibold">{formatDate(subscription.next_billing_date)}</div>
          </div>
          <div>
            <div className="text-blue-100 text-sm mb-1">Payment Method</div>
            <div className="font-semibold flex items-center">
              <CreditCard className="w-4 h-4 mr-2" />
              {subscription.payment_method.brand} •••• {subscription.payment_method.last4}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <Button 
            onClick={() => setShowChangePlanModal(true)}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            Change Plan
          </Button>
          <Button 
            onClick={() => setShowPaymentModal(true)}
            variant="outline" 
            className="border-white text-white hover:bg-blue-700"
          >
            Update Payment Method
          </Button>
        </div>
      </div>

      {/* Plan Features */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Plan Includes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">5 User Accounts</div>
              <div className="text-sm text-gray-600">Add your team members</div>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Unlimited Estimates</div>
              <div className="text-sm text-gray-600">Generate as many as you need</div>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">No Branding</div>
              <div className="text-sm text-gray-600">White-label proposals</div>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Email Support</div>
              <div className="text-sm text-gray-600">Get help when you need it</div>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Satellite Measurements</div>
              <div className="text-sm text-gray-600">Accurate roof data</div>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-gray-900">Digital Signatures</div>
              <div className="text-sm text-gray-600">E-sign contracts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-gray-600" />
            Billing History
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(invoice.date)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.period}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {invoice.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Button variant="ghost" size="sm">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel Subscription */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Subscription</h3>
        <p className="text-gray-600 mb-4">
          Need to cancel? We're sorry to see you go. You can cancel anytime and will have access until the end of your billing period.
        </p>
        <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
          Cancel Subscription
        </Button>
      </div>

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Change Your Plan</h2>
              <button onClick={() => setShowChangePlanModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-800 font-semibold">
                🔥 Presale Pricing Active Until November 15, 11:59 PM
              </p>
              <p className="text-xs text-orange-700 mt-1">
                50% off setup fees + special monthly rates for first 3 months
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Starter Plan */}
              <div className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${selectedPlan === 'starter' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                   onClick={() => setSelectedPlan('starter')}>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Starter</h3>
                  <p className="text-xs text-gray-600 mb-2">Small home service pros</p>
                  <div className="mt-2">
                    <div className="text-sm text-green-600 font-semibold mb-1">Presale: $199/mo x 3 months</div>
                    <div className="text-xs text-gray-500 line-through">Regular: $299/month</div>
                    <div className="text-xs text-gray-500 mt-1">Setup: $250 (50% off)</div>
                  </div>
                  <div className="mt-2 bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">
                    Save $550 Total
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />2 Users</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Unlimited Estimates</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Basic Support</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />"Created by HomeFixer360"</li>
                </ul>
              </div>

              {/* Pro Plan */}
              <div className={`border-2 rounded-lg p-6 cursor-pointer transition-all relative ${selectedPlan === 'pro' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                   onClick={() => setSelectedPlan('pro')}>
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg">
                  Most Popular
                </div>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Professional</h3>
                  <p className="text-xs text-gray-600 mb-2">Growing businesses</p>
                  <div className="mt-2">
                    <div className="text-sm text-green-600 font-semibold mb-1">Presale: $333/mo x 3 months</div>
                    <div className="text-xs text-gray-500 line-through">Regular: $499/month</div>
                    <div className="text-xs text-gray-500 mt-1">Setup: $250 (50% off)</div>
                  </div>
                  <div className="mt-2 bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">
                    Save $750 Total
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />5 Users</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Unlimited Estimates</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Email Support</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />No Branding</li>
                </ul>
              </div>

              {/* Enterprise Plan */}
              <div className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${selectedPlan === 'enterprise' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                   onClick={() => setSelectedPlan('enterprise')}>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Enterprise</h3>
                  <p className="text-xs text-gray-600 mb-2">Large home service companies</p>
                  <div className="mt-2">
                    <div className="text-sm text-green-600 font-semibold mb-1">Presale: $774/mo x 3 months</div>
                    <div className="text-xs text-gray-500 line-through">Regular: $999/month</div>
                    <div className="text-xs text-gray-500 mt-1">Setup: $250 (50% off)</div>
                  </div>
                  <div className="mt-2 bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">
                    Save $925 Total
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Unlimited Users</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Unlimited Estimates</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />Priority Phone Support</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />No Branding</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-2" />API Access</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowChangePlanModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  alert(`Plan change to ${selectedPlan} initiated. This feature will process the change.`);
                  setShowChangePlanModal(false);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Change to {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Update Payment Method Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Update Payment Method</h2>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form className="space-y-4" onSubmit={(e) => {
              e.preventDefault();
              alert('Payment method updated successfully!');
              setShowPaymentModal(false);
            }}>
              <div>
                <Label htmlFor="cardNumber">Card Number</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="cardNumber"
                    type="text"
                    placeholder="4242 4242 4242 4242"
                    value={paymentInfo.cardNumber}
                    onChange={(e) => setPaymentInfo({...paymentInfo, cardNumber: e.target.value})}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expiry">Expiry Date</Label>
                  <Input
                    id="expiry"
                    type="text"
                    placeholder="MM/YY"
                    value={paymentInfo.expiry}
                    onChange={(e) => setPaymentInfo({...paymentInfo, expiry: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cvc">CVC</Label>
                  <Input
                    id="cvc"
                    type="text"
                    placeholder="123"
                    value={paymentInfo.cvc}
                    onChange={(e) => setPaymentInfo({...paymentInfo, cvc: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="name">Cardholder Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={paymentInfo.name}
                  onChange={(e) => setPaymentInfo({...paymentInfo, name: e.target.value})}
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <p>Your payment information is securely processed. We never store your full card details.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Update Payment
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
